/**
 * "Purchase requests" block on /orders: every Buy Request the customer placed
 * (AUCNET STOCK or this portal) as cards, action-needed first, with
 * All / Action needed / In progress / Completed filters.
 *
 * Fetches independently of the won-vehicle list below it, so a failure here
 * never hides the customer's orders (and vice versa).
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, CardTitle, Spinner } from '../ui';
import { STOCK_HREF } from '../chrome/chromeNav';
import { listPurchaseRequests, type PurchaseRequest } from '../../lib/purchaseRequestsApi';
import {
  GROUP_LABELS,
  purchaseRequestStatus,
  sortPurchaseRequests,
  type PurchaseRequestGroup,
} from '../../lib/purchaseRequestStatus';
import { PurchaseRequestCard } from './PurchaseRequestCard';

type Filter = PurchaseRequestGroup | 'all';
const FILTERS: Filter[] = ['all', 'action', 'progress', 'done'];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'success'; items: PurchaseRequest[] };

export function PurchaseRequestsSection(): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    const ctrl = new AbortController();
    listPurchaseRequests(ctrl.signal)
      .then((items) => setLoad({ kind: 'success', items: sortPurchaseRequests(items) }))
      .catch(() => {
        if (ctrl.signal.aborted) return;
        console.error('[PurchaseRequestsSection] buy-requests fetch failed');
        setLoad({ kind: 'error' });
      });
    return () => ctrl.abort();
  }, []);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: 0, action: 0, progress: 0, done: 0 };
    if (load.kind !== 'success') return c;
    for (const r of load.items) {
      c.all += 1;
      c[purchaseRequestStatus(r.state).group] += 1;
    }
    return c;
  }, [load]);

  const visible =
    load.kind === 'success'
      ? load.items.filter(
          (r) => filter === 'all' || purchaseRequestStatus(r.state).group === filter,
        )
      : [];

  return (
    <Card aria-label="Purchase requests">
      <CardHeader>
        <CardTitle level={2}>Purchase requests</CardTitle>
      </CardHeader>
      <CardBody>
        {load.kind === 'loading' ? (
          <div aria-busy="true" data-testid="purchase-requests-loading">
            <Spinner block label="Loading purchase requests" />
          </div>
        ) : null}

        {load.kind === 'error' ? (
          <p
            data-testid="purchase-requests-error"
            style={{ margin: 0, fontSize: '0.875rem', color: '#991b1b' }}
          >
            Purchase requests could not be loaded right now. Please reload the page in a moment.
          </p>
        ) : null}

        {load.kind === 'success' && load.items.length === 0 ? (
          <p
            data-testid="purchase-requests-empty"
            style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563' }}
          >
            No purchase requests yet. When you press “Buy Request” on{' '}
            <a href={STOCK_HREF} style={{ color: '#1f4ed8', fontWeight: 600 }}>
              AUCNET STOCK
            </a>
            , the vehicle appears here with its progress.
          </p>
        ) : null}

        {load.kind === 'success' && load.items.length > 0 ? (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div
              role="group"
              aria-label="Filter purchase requests"
              style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
            >
              {FILTERS.map((f) => {
                const active = f === filter;
                return (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={active}
                    data-testid={`purchase-requests-filter-${f}`}
                    onClick={() => setFilter(f)}
                    style={{
                      minHeight: '2.25rem',
                      padding: '0.375rem 0.875rem',
                      borderRadius: '9999px',
                      border: `1px solid ${active ? '#1e40af' : '#d1d5db'}`,
                      background: active ? '#1f4ed8' : '#fff',
                      color: active ? '#fff' : '#111827',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {GROUP_LABELS[f]} ({counts[f]})
                  </button>
                );
              })}
            </div>
            {visible.length === 0 ? (
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563' }}>
                No purchase requests in “{GROUP_LABELS[filter]}”.
              </p>
            ) : (
              <div
                data-testid="purchase-requests-list"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 17.5rem), 1fr))',
                  gap: '1rem',
                }}
              >
                {visible.map((r) => (
                  <PurchaseRequestCard key={r.buyRequestId} request={r} />
                ))}
              </div>
            )}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
